import type { CSSProperties } from "react";

interface ReadingSceneProps {
  className?: string;
}

const handwriting = {
  fontFamily: "var(--font-mono)",
  fontSize: 17,
  fill: "var(--ink)",
} as const;

/**
 * The hero. A page of homework is read: a light sweeps down it, a marigold tick
 * draws itself beside the answer that is right, and the answer that is not gets
 * a clay underline and a named cause. One orchestrated moment, on a loop.
 * With prefers-reduced-motion the finished state renders and nothing moves.
 */
export function ReadingScene({ className }: ReadingSceneProps) {
  return (
    <svg
      viewBox="0 0 360 320"
      fill="none"
      className={className}
      role="img"
      aria-label="A page of maths homework being read. One answer is ticked, one is underlined with its cause named."
    >
      <defs>
        <linearGradient id="mh-scanbeam" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--marigold)" stopOpacity="0" />
          <stop offset="60%" stopColor="var(--marigold)" stopOpacity="0.92" />
          <stop offset="100%" stopColor="var(--marigold)" stopOpacity="0" />
        </linearGradient>
        <clipPath id="mh-pageclip">
          <rect x="64" y="34" width="196" height="228" rx="12" />
        </clipPath>
      </defs>

      {/* The page underneath, so the stack reads as more than one sheet. */}
      <rect
        x="46" y="46" width="196" height="228" rx="12"
        fill="var(--sage-tint)" stroke="var(--sage-deep)" strokeWidth="2.2"
        transform="rotate(-6 144 160)"
      />

      {/* The page being read. */}
      <g transform="rotate(2.4 162 148)">
        <rect x="64" y="34" width="196" height="228" rx="12" fill="var(--white)" stroke="var(--ink)" strokeWidth="2.5" />

        {/* An exercise book has a margin. So does this. */}
        <path d="M88 34v228" stroke="var(--clay)" strokeWidth="1.4" opacity="0.5" />

        <g clipPath="url(#mh-pageclip)">
          <path d="M100 62h130M100 78h96" stroke="var(--border-soft)" strokeWidth="3.4" strokeLinecap="round" />

          <text x="100" y="118" {...handwriting}>7 × 8 =</text>
          <text x="180" y="118" {...handwriting}>56</text>

          <text x="100" y="164" {...handwriting}>0.649 =</text>
          <text x="180" y="164" {...handwriting} fill="var(--clay)">1.000</text>

          <path d="M100 194h130M100 210h74M100 226h108" stroke="var(--border-soft)" strokeWidth="3.4" strokeLinecap="round" />

          {/* The beam that reads it. */}
          <g className="anim-scan" style={{ transformBox: "view-box", "--scan-travel": "210px" } as CSSProperties}>
            <rect x="64" y="36" width="196" height="42" fill="url(#mh-scanbeam)" />
            <path d="M64 78h196" stroke="var(--marigold)" strokeWidth="3" />
          </g>
        </g>

        {/* Right, and ticked. Marigold at full strength, keylined in ink the way
            every drawing in this cast is, so it carries at a glance. */}
        <g className="anim-pop" style={{ "--delay": "700ms" } as CSSProperties}>
          <path
            d="m214 110 8 9 17-21"
            stroke="var(--ink)" strokeWidth="10" strokeLinecap="round" strokeLinejoin="round"
            className="draw draw-loop"
            style={{ "--len": 46, "--delay": "900ms" } as CSSProperties}
          />
          <path
            d="m214 110 8 9 17-21"
            stroke="var(--marigold)" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"
            className="draw draw-loop"
            style={{ "--len": 46, "--delay": "900ms" } as CSSProperties}
          />
        </g>

        {/* Wrong, and the cause is named rather than the child. */}
        <path
          d="M178 176c15-4 32-3 44 2"
          stroke="var(--clay)" strokeWidth="2.2" strokeLinecap="round"
          className="draw draw-loop"
          style={{ "--len": 52, "--delay": "1300ms" } as CSSProperties}
        />
      </g>

      {/* What the read gives back, as a card the parent would actually read. */}
      <g className="anim-rise" style={{ "--delay": "1050ms" } as CSSProperties}>
        <rect x="112" y="244" width="236" height="62" rx="14" fill="var(--white)" stroke="var(--ink)" strokeWidth="2.5" />
        <rect x="124" y="256" width="10" height="38" rx="5" fill="var(--clay)" />
        <text x="144" y="271" fontFamily="var(--font-body)" fontSize="13.5" fontWeight="600" fill="var(--ink)">
          Rounded to a whole number
        </text>
        <text x="144" y="290" fontFamily="var(--font-mono)" fontSize="13" fill="var(--slate)">
          wrote 1.000
        </text>
        <text x="244" y="290" fontFamily="var(--font-mono)" fontSize="13" fill="var(--slate)">
          answer 0.6
        </text>
      </g>

      {/* Camera brackets: this began as a photograph. */}
      <path
        d="M8 58V28a10 10 0 0 1 10-10h26M352 58V28a10 10 0 0 0-10-10h-26M8 226v30a10 10 0 0 0 10 10h26"
        fill="none" stroke="var(--ink)" strokeWidth="2.5" strokeLinecap="round" opacity="0.5"
      />
    </svg>
  );
}
